import { ELIGIBILITY_STATUS } from '@/types/eligibility';
import { PLANNING_STATUS, PROCEDURE_STATUS } from '@/types/procedures/status';
import { z } from 'zod';
import { ProtocolWithCoordinatesSchema } from '@/api/schema/protocol';

export const ProcedureType = {
  CLINICAL: 'clinical',
  ASSESSMENT: 'assessment',
} as const;

export enum ProcedureFileType {
  NIFTI = 'NIFTI',
  DICOM_2D = 'DICOM-2D',
  DICOM_3D = 'DICOM-3D',
  OTHER = 'OTHER',
}

export const CoordinateDataSchema = z.object({
  ap: z.number(),
  rl: z.number(),
  si: z.number(),
});

const CreatedBySchema = z.object({
  id: z.string(),
  name: z.string().optional().nullable(),
  givenName: z.string().optional().nullable(),
  familyName: z.string().optional().nullable(),
  email: z.string().optional(),
  site: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
  role: z
    .object({
      code: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
});

const DicomMetadataSchema = z.object({
  patientBirthDate: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  scanDate: z.string().nullable(),
  studyId: z.string().nullable(),
});

export const FileSchema = z.object({
  id: z.string(),
  name: z.string(),
  thumbnailUrl: z.string().nullable(),
  previewUrl: z.string().nullable(),
  fileUrl: z.string(),
  metadata: DicomMetadataSchema.nullable(),
  createdBy: CreatedBySchema.nullable(),
  createdAt: z.string().optional().nullable(),
  attributes: z
    .object({
      fileType: z.nativeEnum(ProcedureFileType),
      seriesInstanceUID: z.string().optional(),
      sopInstanceUID: z.string().optional(),
      hasPixelData: z.boolean().optional(),
      numberOfFrames: z.number().optional(),
    })
    .optional()
    .nullable(),
});

export const FilesResponseSchema = z.object({
  items: z.array(FileSchema),
});

export const ProcedureListItemSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(PROCEDURE_STATUS),
  procedureDate: z.string().nullable(),
  planningStatus: z.nativeEnum(PLANNING_STATUS),
  patient: z.object({
    id: z.string(),
    humanReadableId: z.string(),
  }),
  subject: z.object({
    id: z.string(),
    humanReadableId: z.string(),
    eligibilityStatus: z.nativeEnum(ELIGIBILITY_STATUS),
  }),
  protocol: z.object({
    id: z.string(),
    name: z.string(),
  }),
  site: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
});

export const ActiveCoordinateSchema = z.object({
  id: z.string(),
  data: CoordinateDataSchema,
  note: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  createdBy: z.object({
    id: z.string(),
    name: z.string(),
  }),
  procedureTarget: z
    .object({
      id: z.string(),
    })
    .optional(),
});

export const ActiveTargetSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  activeCoordinate: ActiveCoordinateSchema.optional().nullable(),
  plannedCoordinate: ActiveCoordinateSchema.optional().nullable(),
});

export const ProcedureUserSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const ProcedureSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ProcedureType),
  config: z
    .object({
      sonicationPower: z.number().nullable(),
    })
    .optional(),
  procedureDate: z.string().nullable(),
  status: z.nativeEnum(PROCEDURE_STATUS),
  planningStatus: z.nativeEnum(PLANNING_STATUS),
  protocol: ProtocolWithCoordinatesSchema.extend({
    coordinateNames: z.array(z.string()).optional(),
  }),
  patient: z.object({
    id: z.string(),
    eligibilityStatus: z.nativeEnum(ELIGIBILITY_STATUS),
    humanReadableId: z.string(),
  }),
  site: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .optional(),
  subject: z.object({
    id: z.string(),
    humanReadableId: z.string(),
  }),
  activeTargets: z.array(ActiveTargetSchema).optional(),
  operator: ProcedureUserSchema.nullish(),
  cueingOperator: ProcedureUserSchema.nullish(),
  isCueingChecklistAccepted: z.boolean(),
  isCockpitChecklistAccepted: z.boolean(),
});

export const CoordinatesHistorySchema = z.array(ActiveCoordinateSchema);

const TargetTagSchema = z.object({
  id: z.string(),
  procedureTarget: z.object({
    id: z.string(),
    name: z.string(),
  }),
  relatedCoordinate: z
    .object({
      id: z.string(),
      data: CoordinateDataSchema,
      note: z.string(),
      createdAt: z.string(),
      updatedAt: z.string().optional(),
      createdBy: CreatedBySchema,
    })
    .nullable(),
});

export const CommentSchema = z.object({
  id: z.string(),
  commentText: z.string(),
  createdAt: z.string(),
  createdBy: CreatedBySchema,
  targetTags: z.array(TargetTagSchema).optional(),
});

export const CommentsListSchema = z.array(CommentSchema);

// Notes
export const ProcedureNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  cueingBlockName: z.string().nullable(),
  sonicationPassOrder: z.number().nullable(),
  createdAt: z.string(),
  createdBy: CreatedBySchema,
});

export const ProcedureNotesListSchema = z.object({
  items: z.array(ProcedureNoteSchema),
});

// Treatment plan

export const SingleTreatmentCoordinateSchema = z.object({
  targetName: z.string(),
  note: z.string().trim().max(500),
  rl: z.number(),
  ap: z.number(),
  si: z.number(),
});

export const UpdateProcedureInputSchema = z.object({
  sonicationPower: z.number().min(40).max(100).optional(),
  note: z.string().max(1000).nullable().optional(),
  coordinates: z.array(SingleTreatmentCoordinateSchema).optional(),
});

export const GetProcedureFileFramesParametersSchema = z.object({
  fileId: z.string().uuid(),
});

export const GetProcedureFileFramesResponseSchema = z.object({
  frameUrls: z.array(z.string()),
  fileType: z.nativeEnum(ProcedureFileType),
});

export type File = z.infer<typeof FileSchema>;
export type FilesResponse = z.infer<typeof FilesResponseSchema>;
export type ProcedureListItem = z.infer<typeof ProcedureListItemSchema>;
export type ProcedureFilesResponse = File[];
export type ActiveCoordinate = z.infer<typeof ActiveCoordinateSchema>;
export type ActiveTarget = z.infer<typeof ActiveTargetSchema>;
export type Procedure = z.infer<typeof ProcedureSchema>;
export type ProcedureUser = z.infer<typeof ProcedureUserSchema>;
export type CoordinatesHistory = z.infer<typeof CoordinatesHistorySchema>;
export type Comment = z.infer<typeof CommentSchema>;
export type CommentsList = z.infer<typeof CommentsListSchema>;
export type TargetTag = z.infer<typeof TargetTagSchema>;
export type ProcedureNote = z.infer<typeof ProcedureNoteSchema>;
export type ProcedureNotesList = z.infer<typeof ProcedureNotesListSchema>;
export type UpdateProcedureInput = z.infer<typeof UpdateProcedureInputSchema>;
export type SingleTreatmentCoordinate = z.infer<
  typeof SingleTreatmentCoordinateSchema
>;
export type GetProcedureFileFramesParameters = z.infer<
  typeof GetProcedureFileFramesParametersSchema
>;
export type GetProcedureFileFramesResponse = z.infer<
  typeof GetProcedureFileFramesResponseSchema
>;
export type DicomMetadata = z.infer<typeof DicomMetadataSchema>;
